import { MarkKind } from '../constants';

const CHIP_BACKGROUND = '#FFFFFF';
const CHIP_BORDER = '#E5E6E6';
export const CHIP_COUNT_COLOR = '#021715';
/** Each glyph wears the colour of the mark it counts. Location pins and committee deals share this near-black. */
const LOCATION_GLYPH_COLOR = '#0B2936';
const DEAL_GLYPH_COLOR = '#0B2936';

export const CLUSTER_GLYPH_ID: Record<MarkKind, string> = {
  [MarkKind.LOCATION]: 'cluster-glyph-location',
  [MarkKind.DEAL]: 'cluster-glyph-deal',
};

export const CLUSTER_GLYPH_CANVAS = 16;
const GLYPH_CENTER = 8;
const GLYPH_STROKE = 1.4;
const PIN_HEAD_CENTER_Y = 6.3;
const PIN_HOLE_RADIUS = 1.5;

/**
 * The locations as a map pin, the shape a location reads as anywhere; the deals as HubSpot's
 * sprocket, the portal they are mirrored from — the same mark the create action wears on the
 * location card.
 */
const GLYPH_PATHS: Record<MarkKind, string> = {
  [MarkKind.LOCATION]:
    `<path d="M${GLYPH_CENTER} 14.6C${GLYPH_CENTER} 14.6 12.7 9.5 12.7 ${PIN_HEAD_CENTER_Y}A4.7 4.7 0 1 0 3.3 ${PIN_HEAD_CENTER_Y}C3.3 9.5 ${GLYPH_CENTER} 14.6 ${GLYPH_CENTER} 14.6Z" stroke="currentColor" stroke-width="${GLYPH_STROKE}" stroke-linejoin="round"/>` +
    `<circle cx="${GLYPH_CENTER}" cy="${PIN_HEAD_CENTER_Y}" r="${PIN_HOLE_RADIUS}" fill="currentColor"/>`,
  [MarkKind.DEAL]:
    `<circle cx="10.8" cy="10.6" r="2.9" stroke="currentColor" stroke-width="${GLYPH_STROKE}"/>` +
    '<circle cx="10.8" cy="3" r="1.3" fill="currentColor"/>' +
    '<circle cx="3.5" cy="3" r="1.3" fill="currentColor"/>' +
    '<circle cx="4" cy="13" r="1.3" fill="currentColor"/>' +
    `<path d="M10.8 6.8V4.5M4.5 3.9 8.7 8.5M5.1 12.5 8.4 10.9" stroke="currentColor" stroke-width="${GLYPH_STROKE}" stroke-linecap="round"/>`,
};

const GLYPH_COLOR: Record<MarkKind, string> = {
  [MarkKind.LOCATION]: LOCATION_GLYPH_COLOR,
  [MarkKind.DEAL]: DEAL_GLYPH_COLOR,
};

export function buildClusterGlyphSvg(kind: MarkKind): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${CLUSTER_GLYPH_CANVAS}" height="${CLUSTER_GLYPH_CANVAS}" viewBox="0 0 16 16" fill="none" color="${GLYPH_COLOR[kind]}">${GLYPH_PATHS[kind]}</svg>`;
}

/** `upTo` drives a `step` expression on `point_count`; the last one is the default, not a stop. */
export const CHIP_SIZE_STEPS = [
  { upTo: 9, px: 13 },
  { upTo: 24, px: 15 },
  { upTo: Number.POSITIVE_INFINITY, px: 17 },
] as const;

const PAD_X = 8;
const PAD_Y = 5;
const BORDER = 1;
const MIN_CONTENT_WIDTH = 12;

export function clusterChipIconId(typeSize: number): string {
  return `cluster-chip-${typeSize}`;
}

/** `stretchX` is the column Mapbox may widen, `content` the box `icon-text-fit` fits the text to. */
export function clusterChipGeometry(typeSize: number) {
  const contentLeft = BORDER + PAD_X;
  const contentRight = contentLeft + MIN_CONTENT_WIDTH;
  const width = contentRight + PAD_X + BORDER;
  const height = BORDER + PAD_Y + typeSize + PAD_Y + BORDER;

  return {
    width,
    height,
    stretchX: [[contentLeft, contentRight]] as [number, number][],
    content: [contentLeft, 0, contentRight, height] as [number, number, number, number],
  };
}

/**
 * Background only — the glyphs and the counts are the layer's text. No shadow: `icon-text-fit`
 * stretches this image, and a gradient in the stretched column smears into a grey halo.
 */
export function buildClusterChipSvg(typeSize: number): string {
  const { width, height } = clusterChipGeometry(typeSize);
  const boxLeft = BORDER / 2;
  const boxTop = BORDER / 2;
  const boxWidth = width - BORDER;
  const boxHeight = height - BORDER;

  // Top-right corner only — `rx` would round all four.
  const radius = 12;
  const right = boxLeft + boxWidth;
  const bottom = boxTop + boxHeight;
  const box = [
    `M ${boxLeft} ${boxTop}`,
    `H ${right - radius}`,
    `A ${radius} ${radius} 0 0 1 ${right} ${boxTop + radius}`,
    `V ${bottom}`,
    `H ${boxLeft}`,
    'Z',
  ].join(' ');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
    <path d="${box}" fill="${CHIP_BACKGROUND}" stroke="${CHIP_BORDER}" stroke-width="${BORDER}"/>
  </svg>`;
}
